import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'

// 다른 팀 앱에 우리 데이터를 열어 주는 곳.
//
// 화면이 문서를 대신한다. 표와 항목 설명을 서버에서 받아 그 자리에서 주소와 예시 코드를
// 만든다 - 따로 문서를 쓰면 표가 바뀔 때 문서만 옛말이 되고, 받아 가는 쪽은 그 옛말을
// 보고 붙였다가 어긋난다.
//
// 열쇠 원문은 만든 그 순간 한 번만 보인다. 저장된 것은 해시라 우리도 다시 못 본다.

const 오늘 = () => new Date().toISOString().slice(0, 10)
const 한달전 = () => {
  const d = new Date(); d.setMonth(d.getMonth() - 1)
  return d.toISOString().slice(0, 10)
}

// 표 정보를 그대로 읽어 만든다. 손으로 적어 두면 표가 바뀔 때 여기만 옛말이 된다.
function 예시코드(ds, 기준주소, 헤더이름, 언어) {
  const q = ds.계좌지정 || ds.처음
    ? `?from=${한달전()}&to=${오늘()}`
    : ''
  const url = `${기준주소}${ds.경로}${q}`
  if (언어 === 'curl') {
    return `curl -s "${url}" \\\n  -H "${헤더이름}: $FA_EXPORT_KEY"`
  }
  if (언어 === 'python') {
    return `import os, requests

r = requests.get(
    "${url}",
    headers={"${헤더이름}": os.environ["FA_EXPORT_KEY"]},   # 열쇠는 코드가 아니라 환경변수에
    timeout=30,
)
r.raise_for_status()
for row in r.json()["rows"]:
    print(row["${(ds.필드[0] || {}).이름 || ''}"])`
  }
  return `// 반드시 서버에서 부를 것 — 브라우저에서 부르면 열쇠가 그대로 새어 나간다
const r = await fetch("${url}", {
  headers: { "${헤더이름}": process.env.FA_EXPORT_KEY },
})
const { count, rows } = await r.json()`
}

function 설명칸({ ds, field, value, 고침, onSave }) {
  const [고치는중, set고치는중] = useState(false)
  const [값, set값] = useState(value)
  useEffect(() => { set값(value) }, [value])
  if (!고치는중) {
    return (
      <span className="dx-note" onClick={() => set고치는중(true)}
        title="눌러서 고칩니다">
        {value || <span className="muted">설명 없음</span>}
        {고침 && <span className="muted" style={{ fontSize: 10 }}> · 고침</span>}
      </span>
    )
  }
  return (
    <span className="dx-note-edit">
      <input value={값} autoFocus onChange={(e) => set값(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { onSave(ds, field, 값); set고치는중(false) }
          if (e.key === 'Escape') { set값(value); set고치는중(false) }
        }} />
      <button className="btn tiny" onClick={() => { onSave(ds, field, 값); set고치는중(false) }}>저장</button>
      <button className="btn secondary tiny" onClick={() => { set값(value); set고치는중(false) }}>취소</button>
    </span>
  )
}

// 카드는 번호를 손으로 적을 수 없다 - 5105*********810 을 보고 어느 카드인지 아는
// 사람은 없다(카드사가 가운데를 가려서 준다). 그래서 계좌와 달리 목록을 띄워 고른다.
// 별칭을 앞에 세우고 번호는 뒤에 작게 둔다 - 사람이 가리는 것은 별칭이다.
function 카드고르기({ 카드목록, 고른카드, set고른카드, 이미열린것 }) {
  const [말, set말] = useState('')
  const 보일것 = (카드목록 || []).filter((c) => {
    const t = 말.trim()
    if (!t) return true
    return (c.alias || '').includes(t) || c.cardNo.includes(t)
      || (c.deptName || '').includes(t)
  })
  const 누르기 = (no) => {
    const n = new Set(고른카드)
    if (n.has(no)) n.delete(no); else n.add(no)
    set고른카드(n)
  }
  // 열쇠에 적혀 있는데 목록에 없는 카드도 보여 준다 - 안 보이면 고치기에서 저장하는
  // 순간 조용히 빠진다.
  const 떠돌이 = [...고른카드].filter((no) => !(카드목록 || []).some((c) => c.cardNo === no))
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="row" style={{ gap: 8, marginBottom: 6 }}>
        <span className="muted" style={{ width: 130 }}>열 카드</span>
        <input placeholder="별칭·부서·번호로 찾기" value={말}
          onChange={(e) => set말(e.target.value)} style={{ width: 220 }} />
        <button type="button" className="btn secondary tiny"
          onClick={() => set고른카드(new Set(보일것.map((c) => c.cardNo)))}>
          보이는 것 전부
        </button>
        <button type="button" className="btn secondary tiny"
          onClick={() => set고른카드(new Set())}>해제</button>
        <span className="muted" style={{ fontSize: 12 }}>
          {고른카드.size ? `${고른카드.size}장 고름` : '고른 카드가 없으면 한 건도 안 나갑니다'}
        </span>
      </div>
      <div className="dx-cards">
        {보일것.map((c) => (
          <label key={c.cardNo} className="dx-pick">
            <input type="checkbox" checked={고른카드.has(c.cardNo)}
              onChange={() => 누르기(c.cardNo)} />
            {c.alias || <span className="muted">이름 없음</span>}
            <span className="muted" style={{ fontSize: 11 }}>
              {' '}· {c.cardNo}{c.건수 ? ` · ${fmt(c.건수)}건` : ''}
            </span>
          </label>
        ))}
        {떠돌이.map((no) => (
          <label key={no} className="dx-pick">
            <input type="checkbox" checked onChange={() => 누르기(no)} />
            {no}<span className="muted" style={{ fontSize: 11 }}> · 목록에 없는 카드</span>
          </label>
        ))}
        {보일것.length === 0 && 떠돌이.length === 0 && (
          <span className="muted" style={{ fontSize: 12 }}>해당하는 카드가 없습니다</span>
        )}
      </div>
      {이미열린것 && (
        <div className="muted" style={{ fontSize: 11 }}>
          고르지 않은 카드는 이 열쇠로 한 건도 나가지 않습니다.
        </div>
      )}
    </div>
  )
}

function 열쇠만들기({ 데이터목록, 카드목록, onDone }) {
  const [이름, set이름] = useState('')
  const [메모, set메모] = useState('')
  const [고른것, set고른것] = useState(new Set())
  const [계좌, set계좌] = useState('')
  const [카드, set카드] = useState(new Set())
  const [아이피, set아이피] = useState('')
  const [만든키, set만든키] = useState(null)
  const [err, setErr] = useState('')
  const [보내는중, set보내는중] = useState(false)

  const 계좌필요 = [...고른것].some((k) => 데이터목록.find((d) => d.key === k)?.계좌지정)
  const 카드필요 = [...고른것].some((k) => 데이터목록.find((d) => d.key === k)?.카드지정)

  const 만들기 = async () => {
    setErr(''); set보내는중(true)
    try {
      const r = await portal.post('/api/export-admin/keys', {
        name: 이름, note: 메모, datasets: [...고른것],
        accounts: 계좌.split(/[\s,]+/).filter(Boolean),
        cards: [...카드],
        allow_ips: 아이피.split(/[\s,]+/).filter(Boolean),
      })
      set만든키(r.key)
      onDone()
    } catch (e) { setErr(e.message) } finally { set보내는중(false) }
  }

  // 만들고 나면 원문을 딱 한 번 보여 준다. 닫으면 우리도 다시 못 본다.
  if (만든키) {
    return (
      <div className="panel dx-newkey">
        <h3>열쇠를 만들었습니다 — 지금 한 번만 보입니다</h3>
        <p className="muted">
          저장된 것은 해시라 이 창을 닫으면 우리도 다시 볼 수 없습니다.
          지금 복사해서 받는 쪽에 안전한 경로로 전달하세요(메신저 평문보다 비밀번호 관리자가 낫습니다).
        </p>
        <div className="dx-key">{만든키}</div>
        <div className="row">
          <button className="btn" onClick={() => navigator.clipboard?.writeText(만든키)}>복사</button>
          <button className="btn secondary" onClick={() => set만든키(null)}>닫기</button>
        </div>
      </div>
    )
  }

  return (
    <div className="panel">
      <h3>열쇠 만들기</h3>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input placeholder="열쇠 이름 (예: so.ermore.ai)" value={이름}
          onChange={(e) => set이름(e.target.value)} style={{ minWidth: 220 }} />
        <input placeholder="어디에 쓰는 열쇠인지" value={메모}
          onChange={(e) => set메모(e.target.value)} style={{ minWidth: 300, flex: 1 }} />
      </div>
      <div style={{ margin: '12px 0' }}>
        <div className="muted" style={{ marginBottom: 6 }}>열 데이터 (읽기만 됩니다)</div>
        {데이터목록.map((d) => (
          <label key={d.key} className={'dx-pick' + (d.열수있음 ? '' : ' off')}
            title={d.열수있음 ? '' : '내가 볼 수 없는 데이터는 열어 줄 수 없습니다'}>
            <input type="checkbox" disabled={!d.열수있음} checked={고른것.has(d.key)}
              onChange={() => {
                const n = new Set(고른것)
                if (n.has(d.key)) n.delete(d.key); else n.add(d.key)
                set고른것(n)
              }} />
            {d.이름}
            {!d.열수있음 && <span className="muted" style={{ fontSize: 11 }}> · 권한 없음</span>}
          </label>
        ))}
      </div>
      {계좌필요 && (
        <div className="row" style={{ marginBottom: 8 }}>
          <span className="muted" style={{ width: 130 }}>열 계좌</span>
          <input placeholder="786101-00-076801  (쉼표나 공백으로 여러 개)" value={계좌}
            onChange={(e) => set계좌(e.target.value)} style={{ flex: 1 }} />
        </div>
      )}
      {카드필요 && (
        <카드고르기 카드목록={카드목록} 고른카드={카드} set고른카드={set카드} />
      )}
      <div className="row" style={{ marginBottom: 8 }}>
        <span className="muted" style={{ width: 130 }}>부를 수 있는 IP</span>
        <input placeholder="223.130.158.17  (비우면 아무 데서나 — 권하지 않습니다)"
          value={아이피} onChange={(e) => set아이피(e.target.value)} style={{ flex: 1 }} />
      </div>
      {err && <div className="error-box">{err}</div>}
      <button className="btn"
        disabled={보내는중 || !이름 || !고른것.size || (카드필요 && !카드.size)}
        onClick={만들기}>
        {보내는중 ? '만드는 중…' : '열쇠 만들기'}
      </button>
    </div>
  )
}

// 이미 나가 있는 열쇠의 범위를 고친다. 열쇠 원문은 그대로라 받는 쪽은 손댈 게 없다 -
// 계좌 하나 더 여는 일에 새 열쇠를 내면 저쪽이 설정을 바꿔야 하고, 옛 열쇠를 지우는
// 것을 잊으면 안 쓰는 열쇠가 살아 남는다.
function 열쇠고치기({ k, 데이터목록, 카드목록, onDone, onCancel }) {
  const [고른것, set고른것] = useState(new Set(k.datasets))
  const [계좌, set계좌] = useState(k.accounts.join(', '))
  const [카드, set카드] = useState(new Set(k.cards || []))
  const [아이피, set아이피] = useState(k.allow_ips.join(', '))
  const [메모, set메모] = useState(k.note || '')
  const [err, setErr] = useState('')
  const [보내는중, set보내는중] = useState(false)
  const 계좌필요 = [...고른것].some((x) => 데이터목록.find((d) => d.key === x)?.계좌지정)
  const 카드필요 = [...고른것].some((x) => 데이터목록.find((d) => d.key === x)?.카드지정)

  const 저장 = async () => {
    setErr(''); set보내는중(true)
    try {
      await portal.post(`/api/export-admin/keys/${k.id}/edit`, {
        datasets: [...고른것],
        accounts: 계좌.split(/[\s,]+/).filter(Boolean),
        cards: [...카드],
        allow_ips: 아이피.split(/[\s,]+/).filter(Boolean),
        note: 메모,
      })
      onDone()
    } catch (e) { setErr(e.message) } finally { set보내는중(false) }
  }

  return (
    <tr>
      <td colSpan={9} style={{ background: 'var(--accent-soft)' }}>
        <div style={{ padding: '10px 4px' }}>
          <b>{k.name} 고치기</b>
          <span className="muted" style={{ fontSize: 12 }}>
            {' '}· 열쇠 원문은 그대로입니다 — 받는 쪽은 바꿀 게 없습니다
          </span>
          <div style={{ margin: '10px 0' }}>
            {데이터목록.map((d) => (
              <label key={d.key}
                className={'dx-pick' + (d.열수있음 || k.datasets.includes(d.key) ? '' : ' off')}>
                <input type="checkbox"
                  disabled={!d.열수있음 && !k.datasets.includes(d.key)}
                  checked={고른것.has(d.key)}
                  onChange={() => {
                    const n = new Set(고른것)
                    if (n.has(d.key)) n.delete(d.key); else n.add(d.key)
                    set고른것(n)
                  }} />
                {d.이름}
              </label>
            ))}
          </div>
          {계좌필요 && (
            <div className="row" style={{ marginBottom: 6 }}>
              <span className="muted" style={{ width: 130 }}>열 계좌</span>
              <input value={계좌} onChange={(e) => set계좌(e.target.value)}
                placeholder="쉼표나 공백으로 여러 개" style={{ flex: 1 }} />
            </div>
          )}
          {카드필요 && (
            <카드고르기 카드목록={카드목록} 고른카드={카드} set고른카드={set카드} 이미열린것 />
          )}
          <div className="row" style={{ marginBottom: 6 }}>
            <span className="muted" style={{ width: 130 }}>부를 수 있는 IP</span>
            <input value={아이피} onChange={(e) => set아이피(e.target.value)} style={{ flex: 1 }} />
          </div>
          <div className="row" style={{ marginBottom: 8 }}>
            <span className="muted" style={{ width: 130 }}>용도</span>
            <input value={메모} onChange={(e) => set메모(e.target.value)} style={{ flex: 1 }} />
          </div>
          {err && <div className="error-box">{err}</div>}
          <button className="btn"
            disabled={보내는중 || !고른것.size || (카드필요 && !카드.size)}
            onClick={저장}>
            {보내는중 ? '저장 중…' : '저장'}
          </button>
          {' '}
          <button className="btn secondary" onClick={onCancel}>취소</button>
        </div>
      </td>
    </tr>
  )
}

// 화면 권한과 내보내기 범위를 따로 두는 칸. 화면은 일을 하라고 주는 것이고 열쇠는
// 그 표를 통째로 밖으로 복사하는 일이다 - 계좌를 봐야 하는 사람이라고 해서 계좌를
// 내보낼 사람인 것은 아니다. 아무것도 안 정하면 예전처럼 화면 권한 그대로다.
function 사람별범위({ onDone }) {
  const [자료, set자료] = useState(null)
  const [err, setErr] = useState('')
  const 불러 = () => portal.get('/api/export-admin/scope').then(set자료).catch((e) => setErr(e.message))
  useEffect(() => { 불러() }, [])

  const 저장 = async (사람, datasets) => {
    setErr('')
    try {
      await portal.post('/api/export-admin/scope', { userId: 사람.id, datasets })
      불러(); onDone && onDone()
    } catch (e) { setErr(e.message) }
  }

  if (err) return <div className="panel"><div className="error-box">{err}</div></div>
  if (!자료) return null
  return (
    <div className="panel">
      <h3>사람마다 내보내기 범위</h3>
      <p className="muted" style={{ marginTop: -6 }}>
        열쇠로 열어 줄 수 있는 표를 사람별로 좁힙니다. 아무것도 정하지 않으면
        <b> 그 사람의 화면 권한 그대로</b>입니다 — 화면을 보는 것과 밖으로 내보내는 것은
        다른 일이라, 둘을 따로 둘 수 있게 했습니다. 여기서 화면 권한을 넘겨 줄 수는 없습니다.
      </p>
      <table>
        <thead>
          <tr>
            <th className="l" style={{ width: 150 }}>사람</th>
            <th className="l">내보내기로 열어 줄 수 있는 표</th>
            <th style={{ width: 150 }}></th>
          </tr>
        </thead>
        <tbody>
          {자료.사람들.map((사람) => {
            const 따로 = 사람.따로정한것
            const 지금 = 따로 || 사람.화면으로열수있는
            return (
              <tr key={사람.id}>
                <td className="l">
                  <b>{사람.이름}</b>
                  {사람.전권 && <span className="muted" style={{ fontSize: 11 }}> · 전권</span>}
                  <div className="muted" style={{ fontSize: 11 }}>
                    {따로 ? `따로 정함${사람.정한이 ? ` · ${사람.정한이}` : ''}` : '화면 권한 그대로'}
                  </div>
                </td>
                <td className="l">
                  {자료.표.map((t) => {
                    const 화면있음 = 사람.화면으로열수있는.includes(t.key)
                    return (
                      <label key={t.key} className={'dx-pick' + (화면있음 ? '' : ' off')}
                        title={화면있음 ? '' : '그 화면 권한이 없어 어차피 못 엽니다'}>
                        <input type="checkbox" disabled={!화면있음}
                          checked={화면있음 && 지금.includes(t.key)}
                          onChange={() => {
                            const n = new Set(지금.filter((x) => 사람.화면으로열수있는.includes(x)))
                            if (n.has(t.key)) n.delete(t.key); else n.add(t.key)
                            저장(사람, [...n])
                          }} />
                        {t.이름}
                      </label>
                    )
                  })}
                </td>
                <td>
                  {따로
                    ? <button className="btn secondary tiny" onClick={() => 저장(사람, null)}>
                        화면 권한 그대로
                      </button>
                    : <button className="btn secondary tiny" onClick={() => 저장(사람, [])}>
                        전부 막기
                      </button>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function DataExport() {
  const [cat, setCat] = useState(null)
  const [keys, setKeys] = useState([])
  const [열쇠덤, set열쇠덤] = useState({})
  const [use, setUse] = useState(null)
  const [고른표, set고른표] = useState(null)
  const [고치는열쇠, set고치는열쇠] = useState(null)
  const [언어, set언어] = useState('curl')
  const [err, setErr] = useState('')

  const 불러오기 = () => {
    portal.get('/api/export-admin/catalog').then(setCat)
      .catch((e) => setErr(e.status === 403
        ? '내보내기 관리는 관리자만 볼 수 있습니다.' : e.message))
    portal.get('/api/export-admin/keys').then((r) => {
      setKeys(r.keys || [])
      set열쇠덤({ 전권: r.전권, 남의것: r.남의것, 바뀐기록: r.바뀐기록 || [] })
    }).catch(() => {})
    portal.get('/api/export-admin/usage').then(setUse).catch(() => {})
  }
  useEffect(불러오기, [])

  const 설명저장 = async (dataset, field, note) => {
    try {
      await portal.post('/api/export-admin/note', { dataset, field, note })
      불러오기()
    } catch (e) { setErr(e.message) }
  }

  const 보는표 = useMemo(
    () => cat?.datasets?.find((d) => d.key === 고른표) || null, [cat, 고른표])

  if (err && !cat) return <><h2>데이터 내보내기</h2><div className="error-box">{err}</div></>
  if (!cat) return <><h2>데이터 내보내기</h2><div className="muted">불러오는 중…</div></>

  return (
    <>
      <h2>데이터 내보내기</h2>
      <p className="muted" style={{ marginTop: -6 }}>
        다른 팀 앱이 받아 갈 수 있는 우리 데이터입니다. 여기 없는 표는 어떤 열쇠로도 나가지 않습니다.
      </p>
      {err && <div className="error-box">{err}</div>}

      <div className="panel dx-guide">
        <h3>받는 쪽에 건네줄 것</h3>
        <p className="muted" style={{ marginTop: -6 }}>
          받는 쪽이 개발자가 아니어도 됩니다 — 아래 주소와 열쇠를 그쪽 AI 에게 주면
          스스로 읽고 붙입니다. <b>가이드도 열쇠가 있어야 열립니다</b> — 표와 항목 이름도
          우리 장부 생김새라 링크만으로는 아무것도 보이지 않습니다.
        </p>
        <div className="dx-code">{`가이드: ${cat.가이드주소}
헤더: ${cat.헤더이름}: <발급받은 열쇠>

아래 주소를 열쇠를 넣어 읽고, 우리 데이터 API 쓰는 법을 익힌 다음 받아 가 줘.
curl -s "${cat.가이드주소}" -H "${cat.헤더이름}: <발급받은 열쇠>"`}</div>
        <button className="btn secondary" style={{ marginTop: 8 }}
          onClick={() => navigator.clipboard?.writeText(
            `가이드: ${cat.가이드주소}\n헤더: ${cat.헤더이름}: <발급받은 열쇠>`)}>
          복사
        </button>
      </div>

      <div className="panel">
        <h3>우리 데이터 목록</h3>
        <table>
          <thead>
            <tr>
              <th className="l">데이터</th>
              <th className="l">화면 권한</th>
              <th>건수</th>
              <th>기간</th>
              <th className="l">비고 (눌러서 고침)</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {cat.datasets.map((d) => (
              <tr key={d.key} className={고른표 === d.key ? 'acct-open' : undefined}>
                <td className="l">
                  <b>{d.이름}</b>
                  <span className="muted" style={{ fontSize: 11 }}> · {d.key}</span>
                  {!d.열수있음 && <span className="muted" style={{ fontSize: 11 }}> · 내 권한 없음</span>}
                </td>
                <td className="l muted" style={{ fontSize: 12 }}>{d.화면이름}</td>
                <td>{d.건수 == null ? '-' : fmt(d.건수)}</td>
                <td className="c muted" style={{ fontSize: 12 }}>
                  {d.처음 ? `${d.처음} ~ ${d.마지막}` : '기간 없음'}
                </td>
                <td className="l">
                  <설명칸 ds={d.key} field="" value={d.설명} 고침={d.설명고침} onSave={설명저장} />
                </td>
                <td>
                  <button className="btn secondary tiny"
                    onClick={() => set고른표(고른표 === d.key ? null : d.key)}>
                    {고른표 === d.key ? '접기' : '가져가는 법'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {보는표 && (
        <div className="panel">
          <h3>{보는표.이름} — 가져가는 법</h3>
          <div className="row" style={{ gap: 6, marginBottom: 10 }}>
            {['curl', 'python', 'node'].map((l) => (
              <button key={l} className={'btn tiny ' + (언어 === l ? '' : 'secondary')}
                onClick={() => set언어(l)}>{l}</button>
            ))}
            <span className="muted" style={{ fontSize: 12 }}>
              열쇠는 <b>{cat.헤더이름}</b> 헤더에 넣습니다 · 한 번에 최대 {cat.최대일수}일
            </span>
          </div>
          <pre className="dx-code">{예시코드(보는표, cat.기준주소, cat.헤더이름, 언어)}</pre>
          <div className="warn" style={{ fontSize: 12, marginBottom: 10 }}>
            ⚠ 반드시 <b>서버에서</b> 부르세요. 브라우저(프론트)에 열쇠를 두면 누구나 볼 수 있습니다.
          </div>
          <h4 style={{ marginBottom: 6 }}>나가는 항목</h4>
          <table style={{ maxWidth: 900 }}>
            <thead><tr>
              <th className="l" style={{ width: 180 }}>항목</th>
              <th className="l">비고 (눌러서 고침)</th>
            </tr></thead>
            <tbody>
              {보는표.필드.map((f) => (
                <tr key={f.이름}>
                  <td className="l"><code>{f.이름}</code></td>
                  <td className="l">
                    <설명칸 ds={보는표.key} field={f.이름} value={f.설명}
                      고침={f.고침} onSave={설명저장} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <열쇠만들기 데이터목록={cat.datasets} 카드목록={cat.카드목록} onDone={불러오기} />

      <div className="panel">
        <h3>열쇠 {keys.length ? `· ${keys.length}개` : ''}</h3>
        <p className="muted" style={{ marginTop: -6, fontSize: 12 }}>
          {열쇠덤.전권
            ? '전권자라 모든 열쇠가 보입니다.'
            : `내가 만든 열쇠만 보입니다${열쇠덤.남의것 ? ` · 남이 만든 ${열쇠덤.남의것}개는 보이지 않습니다` : ''}.`}
        </p>
        <table>
          <thead>
            <tr>
              <th className="l">이름</th><th className="l">여는 데이터</th>
              <th className="l">계좌</th><th className="l">카드</th><th className="l">IP</th>
              <th>가져간 횟수</th><th className="l">마지막</th>
              <th className="l">용도</th><th style={{ width: 130 }}></th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr><td colSpan={9} className="c muted">아직 만든 열쇠가 없습니다</td></tr>
            )}
            {keys.map((k) => (
              <tr key={k.id} style={k.active ? undefined : { opacity: 0.5 }}>
                <td className="l">
                  <b>{k.name}</b>
                  {!k.active && <span className="muted" style={{ fontSize: 11 }}> · 꺼짐</span>}
                  <span className="muted" style={{ fontSize: 11 }}> · {k.mode === 'read' ? '읽기만' : k.mode}</span>
                </td>
                <td className="l" style={{ fontSize: 12 }}>
                  {k.datasets.map((d) => cat.datasets.find((x) => x.key === d)?.이름 || d).join(', ')}
                </td>
                <td className="l muted" style={{ fontSize: 12 }}>{k.accounts.join(', ') || '-'}</td>
                <td className="l muted" style={{ fontSize: 12 }}>
                  {(k.cards || []).length
                    ? (() => {
                        const 이름들 = k.cards.map((no) =>
                          (cat.카드목록 || []).find((c) => c.cardNo === no)?.alias || no)
                        return (
                          <span title={이름들.join(', ')}>
                            {이름들.slice(0, 2).join(', ')}
                            {이름들.length > 2 && ` 외 ${이름들.length - 2}장`}
                          </span>
                        )
                      })()
                    : '-'}
                </td>
                <td className="l muted" style={{ fontSize: 12 }}>
                  {k.allow_ips.join(', ') || <span className="warn">제한 없음</span>}
                </td>
                <td>{fmt(k.가져간횟수)}</td>
                <td className="l muted" style={{ fontSize: 12 }}>
                  {k.last_used_at ? String(k.last_used_at).slice(0, 16).replace('T', ' ') : '쓴 적 없음'}
                </td>
                <td className="l muted" style={{ fontSize: 12 }}>{k.note || '-'}</td>
                <td>
                  <button className="btn secondary tiny"
                    onClick={() => set고치는열쇠(고치는열쇠 === k.id ? null : k.id)}>
                    {고치는열쇠 === k.id ? '접기' : '고치기'}
                  </button>
                  {' '}
                  <button className="btn secondary tiny" onClick={async () => {
                    await portal.post(`/api/export-admin/keys/${k.id}/active`, { active: !k.active })
                    불러오기()
                  }}>{k.active ? '끄기' : '켜기'}</button>
                  {' '}
                  <button className="btn secondary tiny" onClick={async () => {
                    if (!window.confirm(`"${k.name}" 열쇠를 지웁니다. 이 열쇠를 쓰던 곳은 바로 막힙니다.`)) return
                    await portal.del(`/api/export-admin/keys/${k.id}`)
                    불러오기()
                  }}>지우기</button>
                </td>
              </tr>
            )).flatMap((줄, i) => {
              const k = keys[i]
              return 고치는열쇠 === k.id
                ? [줄, <열쇠고치기 key={`e${k.id}`} k={k} 데이터목록={cat.datasets}
                    카드목록={cat.카드목록}
                    onDone={() => { set고치는열쇠(null); 불러오기() }}
                    onCancel={() => set고치는열쇠(null)} />]
                : [줄]
            })}
          </tbody>
        </table>
      </div>

      {use?.묶음?.length > 0 && (
        <div className="panel">
          <h3>가져간 기록</h3>
          <table style={{ maxWidth: 900 }}>
            <thead><tr>
              <th className="l">열쇠</th><th className="l">데이터</th>
              <th>횟수</th><th>줄수</th><th className="l">마지막</th>
            </tr></thead>
            <tbody>
              {use.묶음.map((r, i) => (
                <tr key={i}>
                  <td className="l">{r.key_name}</td>
                  <td className="l">{cat.datasets.find((x) => x.key === r.dataset)?.이름 || r.dataset || '-'}</td>
                  <td>{fmt(r.횟수)}</td>
                  <td>{fmt(r.줄수)}</td>
                  <td className="l muted" style={{ fontSize: 12 }}>
                    {String(r.마지막 || '').slice(0, 16).replace('T', ' ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {열쇠덤.전권 && <사람별범위 onDone={불러오기} />}

      {열쇠덤.전권 && 열쇠덤.바뀐기록?.length > 0 && (
        <div className="panel">
          <h3>범위가 바뀐 기록</h3>
          <p className="muted" style={{ marginTop: -6, fontSize: 12 }}>
            「가져간 기록」이 <b>무엇이 나갔나</b>라면 이쪽은 <b>누가 무엇을 열어 줬나</b>입니다.
            이게 없으면 전 계좌가 열려 있어도 언제부터 그랬는지 알 수 없습니다.
          </p>
          <table style={{ maxWidth: 1100 }}>
            <thead><tr>
              <th className="l" style={{ width: 150 }}>언제</th>
              <th className="l" style={{ width: 90 }}>누가</th>
              <th className="l" style={{ width: 160 }}>열쇠</th>
              <th className="l" style={{ width: 80 }}>한 일</th>
              <th className="l">바뀐 내용</th>
            </tr></thead>
            <tbody>
              {열쇠덤.바뀐기록.map((r, i) => (
                <tr key={i}>
                  <td className="l muted" style={{ fontSize: 12 }}>
                    {String(r.at || '').slice(0, 16).replace('T', ' ')}
                  </td>
                  <td className="l">{r.who || '-'}</td>
                  <td className="l">{r.key_name || '-'}</td>
                  <td className="l">{r.한일}</td>
                  <td className="l muted" style={{ fontSize: 11 }}>
                    {r.전 && <div>전 · {r.전}</div>}
                    {r.후 && <div>후 · {r.후}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
